﻿import React, { FC, useCallback } from 'react';
import { IAdminStudyDto } from '../../../../services/api/api-client';
import { useTranslation } from 'react-i18next';
import { useCommonLocalization } from '../../../../application/localisation/useCommonLocalization';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '../../../../application/hooks/useModal';
import Style from '../studyList.module.css';
import { ExpandedRowHeader } from '../../../../components/expandedRow/header/expandedRowHeader.component';
import { AppButton, SmallButton } from 'uikit/buttons';
import { formatRelative } from 'date-fns';
import { getCurrentDateLocale } from '../../../../application/localisation/localization';
import { AppModalContainer } from 'uikit/modal/modal.component';
import { EditStudySyncSettingsModalContent } from '../EditStudySyncSettingsModalContent';
import { ReactComponent as SyncIcon } from 'src/assets/img/patient/indicators/sync.svg';
import { ReactComponent as EditIcon } from 'src/assets/img/common/edit_20.svg';
import ExpandedRowSections from 'uikit/table/ExpandedRowSections';
import PropertiesList from 'uikit/propertiesList/PropertiesList';
import { isStudySyncInProgress, syncStudyDataByAdmin } from 'src/services/api/api-client/StudyClient';
import { getAdminStudiesQueryKey } from 'src/services/api/api-client/StudyQuery';

export const StudySettingsSection: FC<{ study: IAdminStudyDto }> = ({ study }) => {
  const { t } = useTranslation('dev');
  const commonLocalizer = useCommonLocalization();
  const queryClient = useQueryClient();
  const settingsModalManager = useModal();

  const pingSyncStateAsync = useCallback(async () => {
    setTimeout(async () => {
      if (await isStudySyncInProgress(study.id)) {
        pingSyncStateAsync();
        return;
      }
      await queryClient.invalidateQueries(getAdminStudiesQueryKey());
    }, 1000);
  }, [queryClient, study.id]);

  const syncStudyData = useCallback(async () => {
    if (study.isSyncInProgress) return;

    await syncStudyDataByAdmin(study.id);
    await queryClient.invalidateQueries(getAdminStudiesQueryKey());
    await pingSyncStateAsync();
  }, [study.isSyncInProgress, study.id, queryClient, pingSyncStateAsync]);

  return (
    <ExpandedRowSections.Section>
      <ExpandedRowHeader
        text={t('Admin.StudyList.Settings.Caption')}
        rightHeader={
          <AppButton
            onClick={() => settingsModalManager.openModal()}
            variant={'icon-link'}
            colorSchema={'primary'}
            text={t('Admin.StudyList.Settings.Action')}
            Icon={EditIcon}
          />
        }
      />
      <PropertiesList>
        <PropertiesList.Property name={t('Admin.StudyList.Settings.PrismaUrl')} value={study.prismaUrl} />
        <PropertiesList.Property
          name={t('Admin.StudyList.Settings.PrismaToken')}
          value={study.isTokenSet ? '**********************' : commonLocalizer('Common_Never')}
        />
        <PropertiesList.Property
          name={t('Admin.StudyList.Settings.IsTokenActive')}
          value={JSON.stringify(study.isTokenActive)}
        />
        <PropertiesList.Property
          name={t('Admin.StudyList.Settings.IsSync')}
          value={
            <>
              {JSON.stringify(study.isSyncInProgress)}
              <div className={Style.syncButton}>
                <SmallButton disabled={study.isSyncInProgress} onPress={syncStudyData} Icon={SyncIcon} />
              </div>
            </>
          }
        />
        <PropertiesList.Property
          name={t('Admin.StudyList.Settings.LastSyncAt')}
          value={
            study.lastSuccessSyncDate
              ? formatRelative(study.lastSuccessSyncDate, Date.now(), getCurrentDateLocale())
              : commonLocalizer('Common_Never')
          }
        />
      </PropertiesList>

      <AppModalContainer
        visible={settingsModalManager.visible}
        onHide={settingsModalManager.closeModal}
        title={t('Admin.StudyList.Settings.Modal.Header')}
      >
        <EditStudySyncSettingsModalContent {...study} onClose={settingsModalManager.closeModal} />
      </AppModalContainer>
    </ExpandedRowSections.Section>
  );
};
